<template>
  <Modal
    content-class=""
    :content-style="{ height: '300px' }"
    form
    offset-menu
    :title="$t('modals.add_to_organisation_report_modal.title')"
    @submit="save"
  >
    <Tabs v-model="currentTab">
      <Tab
        :label="
          $t('modals.add_to_organisation_report_modal.existing_report_tab')
        "
        name="existing"
      />
      <Tab
        :label="$t('modals.add_to_organisation_report_modal.new_report_tab')"
        name="new"
      />
    </Tabs>
    <div class="tw:p-4">
      <template v-if="currentTab === 'existing'">
        <InputSelect
          v-model="selectedReport"
          :disabled="loadingFolders"
          :label="
            $t('modals.add_to_organisation_report_modal.select_report_label')
          "
          :loading="loadingReports || loadingFolders"
          new-design
          option-label="label"
          option-value="id"
          :options="filteredReports"
          use-input
          @filter="filterReports"
        />
        <Checkbox
          v-model="drafts"
          :label="
            $t('modals.add_to_organisation_report_modal.toggle_drafts_label')
          "
          new-design
        />
        <template v-if="selectedReport">
          <div class="tw:mt-4 tw:grid tw:grid-cols-2">
            <Radio
              v-model="createFolder"
              :label="
                $t(
                  'modals.add_to_organisation_report_modal.select_existing_folder_option'
                )
              "
              new-design
              :value="false"
            />
            <Radio
              v-model="createFolder"
              :label="
                $t(
                  'modals.add_to_organisation_report_modal.select_new_folder_option'
                )
              "
              new-design
              value
            />
          </div>
          <template v-if="createFolder">
            <InputText
              ref="newFolderInput"
              v-model="selectedFolder.label"
              class="tw:mt-4"
              :label="newFolderLabel"
              new-design
              :placeholder="
                $t(
                  'modals.add_to_organisation_report_modal.new_folder_placeholder'
                )
              "
              type="text"
            />
          </template>
          <template v-else>
            <InputSelect
              v-model="selectedFolder"
              class="tw:mt-4"
              :disabled="loadingFolders"
              :label="folderLabel"
              :loading="loadingFolders"
              new-design
              option-label="label"
              option-value="id"
              :options="filteredFolders"
              use-input
              :validations="[validateMentionsToSave]"
              @filter="filterFolders"
            />
          </template>
        </template>
      </template>
      <template v-else>
        <InputText
          v-model="newReportSubject"
          :label="newReportLabel"
          new-design
        />
        <div
          v-if="canSummariseMentions"
          class="tw:mt-6"
        >
          <label class="field-heading">
            {{
              $t(
                `modals.add_to_organisation_report_modal.article_summarisation`
              )
            }}
          </label>
          <div class="tw:flex tw:gap-x-4">
            <Radio
              v-model="summaryType"
              :label="
                $t('modals.add_to_organisation_report_modal.no_summaries')
              "
              new-design
              value="none"
            />
            <Radio
              v-model="summaryType"
              :label="
                $t('modals.add_to_organisation_report_modal.short_summary')
              "
              new-design
              value="short"
            />
            <Radio
              v-model="summaryType"
              :label="
                $t('modals.add_to_organisation_report_modal.long_summary')
              "
              new-design
              value="long"
            />
          </div>
        </div>
        <InputText
          v-model="newReportFolderLabel"
          class="tw:mt-6"
          :label="newFolderLabel"
          new-design
        />
      </template>
    </div>
    <template #buttons>
      <BaseButton
        v-if="!$isMobile"
        color="white"
        :label="$t('global.cancel')"
        new-design
        @click.prevent="$modals.close('AddToOrganisationReportModal')"
      />
      <BaseButton
        :aria-label="$t('global.save')"
        color="primary"
        :disabled="!isFormValid || loadingFolders"
        :label="
          $t('modals.add_to_organisation_report_modal.add_to_report_button')
        "
        new-design
        type="submit"
      />
    </template>
  </Modal>
</template>

<script>
import { useDesktopStore } from "desktop/src/stores/desktop";
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";

import {
  BaseButton,
  InputSelect,
  InputText,
  Tab,
  Tabs,
} from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import { formatIntlDate } from "shared/helpers/date";
import { summarise } from "shared/helpers/mentions";
import OrganisationReportService from "shared/services/api/OrganisationReportService";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";

export default {
  name: "AddToOrganisationReportModal",
  components: {
    Modal,
    BaseButton,
    InputSelect,
    InputText,
    Tabs,
    Tab,
  },
  props: {
    mentions: {
      type: [Object, Array],
      required: true,
    },
    stream: {
      type: Object,
      default: null,
    },
  },
  emits: ["success"],
  setup() {
    const desktopStore = useDesktopStore();
    const { addMentionToOrganisationReport } = desktopStore;

    const userStore = useUserStore();
    const { adminUserEnabled } = storeToRefs(userStore);

    const { selectedTeamPreference } = useTeamPicker();

    const globalStore = useGlobalStore();
    const { isCisionOne } = storeToRefs(globalStore);

    return {
      addMentionToOrganisationReport,
      adminUserEnabled,
      selectedTeamPreference,

      isCisionOne,
    };
  },
  data() {
    return {
      currentTab: "existing",
      createFolder: false,
      loadingReports: false,
      loadingFolders: false,
      selectedReport: null,
      selectedFolder: null,
      newReportSubject: "",
      newReportFolderLabel: "",
      folders: [],
      organisationReports: [],
      drafts: true,
      debouncedLoadReports: debounce(this.loadReports, 500),
      reportsFilter: "",
      foldersFilter: "",
      summaryType: "none",
    };
  },
  computed: {
    mentionsCount() {
      return this.mentions.length;
    },
    isFormValid() {
      return (
        (this.selectedReport && this.selectedFolder) ||
        (this.newReportSubject && this.newReportFolderLabel)
      );
    },
    mention() {
      return this.mentions;
    },
    mentionsToSave() {
      if (this.mentions instanceof Array) {
        return this.mentions.map((mention) => ({
          ...mention,
          selectedSyndication: mention.id,
          sourceStream: this.stream,
        }));
      }

      return [
        {
          ...this.mention,
          selectedSyndication: this.mention.id,
          sourceStream: this.stream,
        },
      ];
    },
    selectReportOptions() {
      return [
        ...this.organisationReports
          .filter((report) => !report.hidden || this.adminUserEnabled)
          .map((der) => ({
            label: this.emailReportLabel(der),
            value: der,
            id: der.id,
          })),
      ];
    },
    selectFolderOptions() {
      return [
        ...this.folders.map((folder) => ({
          label: this.$t(
            "modals.add_to_organisation_report_modal.items_count",
            { folderLabel: folder.label, itemsCount: folder.items.length }
          ),
          value: folder,
          id: folder.id,
        })),
      ];
    },
    filteredReports() {
      if (this.reportsFilter) {
        const filterRegexp = new RegExp(this.reportsFilter, "i");

        return this.selectReportOptions.filter((option) =>
          filterRegexp.test(option.label)
        );
      }

      return this.selectReportOptions;
    },
    filteredFolders() {
      if (this.foldersFilter) {
        const filterRegexp = new RegExp(this.foldersFilter, "i");

        return this.selectFolderOptions.filter((option) =>
          filterRegexp.test(option.label)
        );
      }

      return this.selectFolderOptions;
    },
    folderLabel() {
      return this.$tc(
        `modals.add_to_organisation_report_modal.select_folder_label`,
        this.mentionsCount
      );
    },
    newReportLabel() {
      return this.$tc(
        `modals.add_to_organisation_report_modal.new_report_label`,
        this.mentionsCount
      );
    },
    newFolderLabel() {
      return this.$tc(
        `modals.add_to_organisation_report_modal.new_folder_label`,
        this.mentionsCount
      );
    },
    canSummariseMentions() {
      return (
        this.isCisionOne ||
        this.$features.has("has_organisation_report_mention_summaries")
      );
    },
  },
  watch: {
    currentTab() {
      this.createFolder = false;
      this.selectedReport = null;
      this.selectedFolder = null;
      this.newReportSubject = "";
      this.newReportFolderLabel = "";
    },
    drafts() {
      this.selectedReport = null;
      this.createFolder = false;
      this.selectedFolder = null;
      this.debouncedLoadReports();
    },
    reportsFilter() {
      this.debouncedLoadReports();
    },
    selectedReport(report) {
      if (report) {
        this.$nextTick(() => {
          this.fetchReportFolders();
        });
      }

      this.selectedFolder = null;
      this.createFolder = false;
    },
    selectedFolder(folder) {
      this.$nextTick(() => {
        if (folder && this.createFolder) {
          this.$refs.newFolderInput.focus();
        }
      });
    },
    createFolder(value) {
      if (value) {
        this.selectedFolder = {
          isNew: true,
          label: "New folder",
          heading_summary: "",
          items: [],
        };
      } else {
        this.selectedFolder = null;
      }
    },
  },
  mounted() {
    this.loadReports();
  },
  methods: {
    async fetchReportFolders() {
      try {
        this.loadingFolders = true;

        this.folders =
          (await OrganisationReportService.show(this.selectedReport.id)).data
            .folders || [];
      } catch (error) {
        this.$error(
          this.$t("modals.add_to_organisation_report_modal.load_folders_error"),
          error
        );
      } finally {
        this.loadingFolders = false;
      }
    },
    async loadReports() {
      this.loadingReports = true;

      this.organisationReports = (
        await OrganisationReportService.index({
          params: {
            include_content: false,
            drafts: this.drafts,
            limit: 20,
            subject: this.reportsFilter,
            ...(this.$features.has("has_team_workspaces")
              ? { organisation_team_id: this.selectedTeamPreference }
              : {}),
          },
        })
      ).data;

      this.loadingReports = false;
    },
    emailReportLabel(report) {
      if (!report) return "";

      return `${
        report.subject ||
        this.$t("modals.add_to_organisation_report_modal.no_subject")
      } - ${formatIntlDate(new Date(report.created_at), {
        dateStyle: "medium",
        timeStyle: "short",
      })}`;
    },
    buildRequest() {
      return {
        folders: OrganisationReportService.buildFolders([
          {
            label: this.newReportFolderLabel,
            order: 0,
            heading_summary: "",
            items: this.mentionsToSave,
          },
        ]),
        subject: this.newReportSubject,
        summary_type: this.summaryType,
        recipients: [],
        message: "",
        sendAs: "me",
        displayExcerpts: false,
        includePdf: true,
        pdfType: "none",
        ...(this.$features.has("has_team_workspaces")
          ? {
              organisation_team_id: this.selectedTeamPreference,
              share_with: "team",
            }
          : {}),
      };
    },
    async save() {
      this.$q.loading.show({ delay: 0 });

      try {
        let targetFolder;
        await this.handleSummarisation(this.mentionsToSave);

        if (this.selectedReport && this.selectedFolder) {
          // attach a new folder to existing report with the item if set
          if (this.selectedFolder.isNew) {
            const { isNew, ...folder } = this.selectedFolder;
            folder.order = this.folders.length;
            folder.items = folder.items.concat(this.mentionsToSave);
            this.folders.push(folder);
            targetFolder = folder;
          } else {
            // attach to existing folder in report
            targetFolder = this.folders.find(
              (existingFolder) => existingFolder === this.selectedFolder.value
            );

            targetFolder.items = targetFolder.items.concat(this.mentionsToSave);
          }

          this.selectedReport.folders = this.folders;

          // selected report is currently open
          if (
            this.$route.name === "curated-email-report" &&
            Number(this.$route.params.editedReportId) ===
              Number(this.selectedReport.id)
          ) {
            const addedMentionOptions = {
              folder: targetFolder,
              mention: this.mention,
            };

            this.addMentionToOrganisationReport(addedMentionOptions);
          } else {
            const { folders } = this.selectedReport;

            await OrganisationReportService.update(this.selectedReport.id, {
              params: {
                folders: OrganisationReportService.buildFolders(folders),
              },
            });
          }
        } else {
          // create new report and folder to attach mention
          const params = this.buildRequest();
          await OrganisationReportService.create({ params });
        }

        this.$emit("success");

        this.$alert(
          this.$t("modals.add_to_organisation_report_modal.mention_saved")
        );

        this.$modals.close("AddToOrganisationReportModal");
      } catch {
        this.$alert(
          this.$t(
            "modals.add_to_organisation_report_modal.problem_saving_mention"
          )
        );
      } finally {
        this.$q.loading.hide();
      }
    },
    filterReports(value) {
      this.reportsFilter = value;
    },
    filterFolders(value) {
      this.foldersFilter = value;
    },
    validateMentionsToSave() {
      if (!this.selectedFolder?.value) return true;

      const { items } = this.selectedFolder.value;

      const hasExistingMentions = this.mentionsToSave.some((mention) =>
        items.some(
          (item) => item.id === mention.id && item.type === mention.type
        )
      );

      if (hasExistingMentions) {
        return "Mention already exists in folder";
      }

      return true;
    },
    async handleSummarisation(mentions) {
      const summaryType =
        this.currentTab === "existing"
          ? this.selectedReport?.value?.summary_type
          : this.summaryType;

      if (["short", "long"].includes(summaryType)) {
        await Promise.all(
          mentions.map((mention) => {
            const isFinancialTimes =
              mention.license === "financial-times" ||
              mention.source?.name === "Financial Times";

            if (!isFinancialTimes) return summarise({ mention, summaryType });

            return Promise.resolve();
          })
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.tabs) {
  border-bottom: 1px solid var(--s-color-denim-2);
}

:deep(.tab) {
  display: flex;
  flex: 1;
  flex-direction: column;
}
</style>
